import React from 'react';

import { BiMessageAdd } from 'react-icons/bi';

import axios from 'helpers/axios';
import config from 'helpers/config';
import ConversationModel from 'models/ConversationModel';
import CustomerPickerModal from 'components/modals/CustomerPickerModal';
import Customer from 'models/CustomerModel';
import ConversationStatusType from 'models/types/ConversationStatusType';
import Avatar from 'react-avatar';
import MessageModel from 'models/MessageModel';
import MessageSenderType from 'models/types/MessageSenderType';
import { FaEnvelope, FaPhoneSquare, FaSearch } from 'react-icons/fa';
import User from 'models/UserModel';
import RelativeTime from 'components/common/RelativeTime';
import moment from 'moment';
// import { BsSend } from 'react-icons/bs';
import { IoMdSend } from 'react-icons/io';
import { Mail, MoreVertical, Phone, Wrench, Menu, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import HoverTooltip from 'components/common/HoverTooltip';

interface Props {}

interface State {
    isCustomerPickerModalOpen: boolean;

    isCreateAppointmentModalOpen: boolean;
    conversations: ConversationModel[];
    filteredConversations: ConversationModel[];
    users: User[];

    selectedConversation: ConversationModel | null;
    isDisabled?: boolean; // Flag to disable the send button
    isMobileSidebarOpen: boolean;
    isMobile: boolean;
}

class TextingPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isCustomerPickerModalOpen: false,
            isCreateAppointmentModalOpen: false,
            conversations: [],
            filteredConversations: [],
            users: [],

            selectedConversation: null,
            isDisabled: true,
            isMobileSidebarOpen: false,
            isMobile: false,
        };

        this.startConversation = this.startConversation.bind(this);
        this.sendMessage = this.sendMessage.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.toggleMobileSidebar = this.toggleMobileSidebar.bind(this);
    }

    handleResize() {
        const isMobile = window.innerWidth < 768;
        this.setState({ isMobile, isMobileSidebarOpen: !isMobile });
    }

    toggleMobileSidebar() {
        this.setState({ isMobileSidebarOpen: !this.state.isMobileSidebarOpen });
    }
    componentDidMount() {
        axios.get(`${config.apiUrl}/messaging/conversations`).then((res) => {
            this.setState({ conversations: res.data, filteredConversations: res.data });
        });

        this.getAllUsers();
        document.body.style.overflowY = 'hidden';

        // Initialize mobile state
        this.handleResize();
        window.addEventListener('resize', this.handleResize);

        // Add CSS for contenteditable placeholder
        const style = document.createElement('style');
        style.textContent = `
            [contenteditable][data-placeholder]:empty::before {
                content: attr(data-placeholder);
                color: #9CA3AF;
                cursor: text;
            }
            [contenteditable][data-placeholder]:empty:focus::before {
                content: "";
            }
        `;
        document.head.appendChild(style);
    }

    componentWillUnmount() {
        document.body.style.overflowY = 'auto';
        window.removeEventListener('resize', this.handleResize);
    }

    getAllUsers() {
        axios.get(`${config.apiUrl}/user`).then((res) => {
            this.setState({ users: res.data });
        });
    }

    openCustomerPicker() {
        this.setState({
            isCustomerPickerModalOpen: true,
        });
    }

    startConversation(customer?: Customer) {
        this.setState({ isCustomerPickerModalOpen: false });
        if (!!!customer) return;

        const newConversation: Partial<ConversationModel> = {
            customerId: customer.id!,
            messages: [],
            status: ConversationStatusType.Active,
            customer,
        };
        this.setState({
            conversations: [newConversation as ConversationModel, ...this.state.conversations],
            filteredConversations: [newConversation as ConversationModel, ...this.state.conversations],
            selectedConversation: newConversation as ConversationModel,
        });
    }

    sendMessage() {
        const { selectedConversation } = this.state;
        // @ts-ignore
        const messageContent = document.getElementById('text-message-content')?.innerText;
        const message: Partial<MessageModel> = {
            senderType: MessageSenderType.Tenant,
            content: messageContent,
        };
        if (!!!selectedConversation?.id) {
            selectedConversation!.messages = [message as MessageModel];

            axios
                .post(`${config.apiUrl}/messaging/conversation`, selectedConversation)
                .then((res) => {
                    const newConversation = res.data as ConversationModel;
                    newConversation.customer = selectedConversation!.customer;
                    newConversation.customerId = selectedConversation!.customerId;
                    this.setState({ isDisabled: true, selectedConversation: newConversation });
                })
                .catch((error) => {
                    console.error('Error starting conversation:', error);
                });
        } else if (selectedConversation.id) {
            axios
                .post(`${config.apiUrl}/messaging/conversation/${selectedConversation.id}`, message)
                .then((res) => {
                    selectedConversation.messages = [res.data, ...selectedConversation.messages];
                    this.setState({ isDisabled: true, selectedConversation });
                })
                .catch((error) => {
                    console.error('Error writing to conversation:', error);
                });
        }
        // @ts-ignore
        document.getElementById('text-message-content').innerHTML = '';
    }

    getSenderUser(message: MessageModel) {
        if (message.senderType === MessageSenderType.Tenant) {
            const user = this.state.users?.find((user) => user.id === message.createdById);
            return (
                <span>
                    {user?.firstName} {user?.lastName}
                </span>
            );
        } else if (message.senderType === MessageSenderType.Customer) {
            const { selectedConversation } = this.state;

            return (
                <span>
                    {selectedConversation?.customer?.firstName} {selectedConversation?.customer?.lastName}
                </span>
            );
        }
    }

    renderMessages() {
        const { selectedConversation } = this.state;
        const messages = selectedConversation?.messages || [];

        const messageGroups = [];
        let currentGroup: any[] = [];

        for (let i = 0; i < messages.length; i++) {
            const message = messages[i];
            const prevMessage = i !== messages.length - 1 ? messages[i + 1] : null;

            if (
                !prevMessage ||
                message.senderType !== prevMessage.senderType ||
                message.createdById !== prevMessage.createdById ||
                // @ts-ignore
                Math.abs(new Date(message.createdAt) - new Date(prevMessage.createdAt)) > 300000 // 5 minutes
            ) {
                // Start a new message group
                currentGroup.push(message);
                messageGroups.push(currentGroup);
                currentGroup = [];
            } else {
                // Add to the current message group
                currentGroup.push(message);
            }
        }

        if (currentGroup.length > 0) {
            messageGroups.push(currentGroup);
        }

        return messageGroups.map((messageGroup, groupIndex) => {
            const firstMessage = messageGroup[0];
            const isFromTenant = firstMessage.senderType === MessageSenderType.Tenant;

            return (
                <div key={groupIndex} className={`flex ${isFromTenant ? 'justify-end' : 'justify-start'} mb-4`}>
                    <div
                        className={`flex ${
                            isFromTenant ? 'flex-row-reverse' : 'flex-row'
                        } items-end space-x-2 max-w-xs lg:max-w-md`}
                    >
                        {/* Avatar */}
                        {!isFromTenant && (
                            // <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                            //     {customerInitials(selectedConversation!.customer)}
                            // </div>
                            <Avatar name={selectedConversation?.customer.fullName} round size={'33'} />
                        )}

                        {/* Message group */}
                        <div
                            className={`flex flex-col-reverse space-y-reverse space-y-1 ${
                                isFromTenant ? 'items-end' : 'items-start'
                            }`}
                        >
                            {messageGroup.map((message, index) => (
                                <div
                                    key={message.id}
                                    className={`group relative px-4 py-2 rounded-2xl max-w-full break-words ${
                                        isFromTenant
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white text-gray-900 border border-gray-200'
                                    } ${
                                        messageGroup.length > 1
                                            ? index === 0
                                                ? isFromTenant
                                                    ? 'rounded-tr-md'
                                                    : 'rounded-tl-md'
                                                : index === messageGroup.length - 1
                                                ? isFromTenant
                                                    ? 'rounded-br-md'
                                                    : 'rounded-bl-md'
                                                : isFromTenant
                                                ? 'rounded-r-md'
                                                : 'rounded-l-md'
                                            : ''
                                    }`}
                                >
                                    <p className="text-sm">{message.content}</p>

                                    {/* Timestamp on hover */}
                                    <div
                                        className={`absolute ${
                                            isFromTenant ? 'right-full mr-2' : 'left-full ml-2'
                                        } top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none`}
                                    >
                                        <span className="text-xs bg-gray-900 text-white px-2 py-1 rounded whitespace-nowrap">
                                            {moment(message.createdAt).format('HH:mm')}
                                        </span>
                                    </div>
                                </div>
                            ))}

                            {/* Sender info and time for the group */}
                            <div className={`text-xs text-gray-500 mt-1 ${isFromTenant ? 'text-right' : 'text-left'}`}>
                                <span className="mr-2">{this.getSenderUser(firstMessage)}</span>
                                <RelativeTime
                                    dateTime={firstMessage.createdAt}
                                    floatRight={false}
                                    displayExactTimePopup={false}
                                />
                            </div>
                        </div>

                        {/* Spacer for tenant messages to maintain alignment */}
                        {isFromTenant && <div className="w-8"></div>}
                    </div>
                </div>
            );
        });
    }

    openConversation(conversation: ConversationModel) {
        this.setState(
            {
                selectedConversation: conversation,
                isDisabled: true,
                isMobileSidebarOpen: !this.state.isMobile, // Close sidebar on mobile when conversation is selected
            },
            () => {
                const inputField = document.getElementById('text-message-content');
                inputField!.removeEventListener('paste', this.stripTextStyleOnPaste);
                inputField!.addEventListener('paste', this.stripTextStyleOnPaste);
                inputField!.innerText = '';
            },
        );
    }

    stripTextStyleOnPaste(e: Event) {
        e.preventDefault();
        //@ts-ignore
        const text = (e.originalEvent || e).clipboardData.getData('text/plain');
        //@ts-ignore
        document.execCommand('insertHTML', false, text);
    }

    filterConversations(searchText: string) {
        const { conversations } = this.state;
        if (!searchText) {
            this.setState({ filteredConversations: conversations });
        }

        const filteredConversations = conversations.filter((conversation) => {
            const customer = conversation.customer;
            if (!customer) {
                return false;
            }
            const fullName = customer.fullName;
            const phoneNumber = customer.phoneNumbers[0]?.phoneNumber;
            return fullName?.toLowerCase().includes(searchText.toLowerCase()) || phoneNumber?.includes(searchText);
        });

        this.setState({ filteredConversations });
    }

    render() {
        const {
            isCustomerPickerModalOpen,
            conversations,
            selectedConversation,
            filteredConversations,
            isMobile,
            isMobileSidebarOpen,
        } = this.state;
        return (
            <div className="bg-white h-full relative">
                {isCustomerPickerModalOpen && <CustomerPickerModal onClose={this.startConversation} />}

                {/* Mobile overlay */}
                {isMobile && isMobileSidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                        onClick={() => this.setState({ isMobileSidebarOpen: false })}
                    />
                )}

                {/* Sidebar */}
                <div
                    className={`
                    ${
                        isMobile
                            ? `fixed inset-y-0 left-0 z-50 w-full bg-white transform transition-transform duration-300 ease-in-out ${
                                  isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                              }`
                            : 'w-80 bg-white border-r border-gray-200 flex-shrink-0 h-screen fixed'
                    }
                    flex flex-col overflow-hidden
                `}
                >
                    {/* Header with title and new message button */}
                    <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h1 className="text-xl font-semibold text-gray-900">Messages</h1>
                            <button
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                                onClick={() => this.openCustomerPicker()}
                                title="Start new chat"
                            >
                                <BiMessageAdd className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Search input */}
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-500"
                                placeholder="Search contacts..."
                                onChange={(e) => this.filterConversations(e.target.value)}
                                id="search-contacts"
                            />
                        </div>
                    </div>
                    {/* Conversations list */}
                    <div className="min-h-96 overflow-y-scroll pb-60">
                        {!!!conversations.length && (
                            <div className="p-4 text-center">
                                <div className="text-gray-400 mb-2">
                                    <BiMessageAdd className="w-12 h-12 mx-auto mb-3" />
                                </div>
                                <p className="text-gray-600 font-medium">No conversations yet</p>
                                <p className="text-gray-400 text-sm">Start a new chat to begin messaging</p>
                            </div>
                        )}
                        {filteredConversations?.map((conversation) => {
                            const isSelected = selectedConversation?.id === conversation?.id;
                            const lastMessage = conversation.messages?.[0];

                            return (
                                <div
                                    className={`p-4 border-b border-gray-100 cursor-pointer transition-all duration-150 hover:bg-gray-50 ${
                                        isSelected ? 'bg-blue-50 border-r-2 border-r-blue-500' : ''
                                    }`}
                                    key={conversation.id}
                                    onClick={() => this.openConversation(conversation)}
                                >
                                    <div className="flex items-start space-x-3">
                                        <div className="relative">
                                            {/* <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                                {customerInitials(conversation.customer)}
                                            </div> */}
                                            <Avatar name={conversation.customer.fullName} round size={'33'} />

                                            {/* Online status indicator */}
                                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <h3 className="font-semibold text-gray-900 truncate">
                                                    {conversation.customer?.fullName}
                                                </h3>
                                                <div className="flex items-center space-x-2">
                                                    {lastMessage && (
                                                        <span className="text-xs text-gray-500">
                                                            <RelativeTime
                                                                dateTime={lastMessage.createdAt}
                                                                floatRight={false}
                                                                displayExactTimePopup={false}
                                                            />
                                                        </span>
                                                    )}
                                                    {/* Unread indicator - you can add logic here */}
                                                    {!isSelected &&
                                                        lastMessage?.senderType === MessageSenderType.Customer && (
                                                            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                                        )}
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm text-gray-600 flex items-center">
                                                    <FaPhoneSquare className="w-3 h-3 mr-1.5 text-gray-400" />
                                                    <span className="truncate">
                                                        {conversation.customer?.phoneNumbers?.[0]?.phoneNumber}
                                                    </span>
                                                </div>
                                                {(conversation.customer?.repairOrders?.length || 0) > 0 && (
                                                    <div className="flex space-x-1">
                                                        {conversation.customer?.repairOrders?.slice(0, 3).map((ro) => (
                                                            <span
                                                                key={ro.id}
                                                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                                            >
                                                                #{ro.repairOrderNumber}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {lastMessage && (
                                                <p className="text-sm text-gray-500 truncate mt-1">
                                                    {lastMessage.senderType === MessageSenderType.Tenant ? 'You: ' : ''}
                                                    {lastMessage.content}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                {/* Main chat area */}
                <div
                    className={`
                    flex flex-col bg-white h-full
                    ${isMobile ? 'w-full' : 'min-w-0 ml-80'}
                `}
                >
                    {/* Mobile header with menu button */}
                    {isMobile && selectedConversation && (
                        <div className="bg-white border-b border-gray-200 p-4 flex items-center space-x-3">
                            <button
                                onClick={() => this.setState({ selectedConversation: null, isMobileSidebarOpen: true })}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-150"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="flex items-center space-x-3">
                                <Avatar name={selectedConversation.customer.fullName} round size={'32'} />
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        {selectedConversation.customer?.fullName}
                                    </h2>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Desktop header with menu button */}
                    {isMobile && !selectedConversation && (
                        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900">Messages</h1>
                            <button
                                onClick={this.toggleMobileSidebar}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-150"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                    {!!!selectedConversation && (
                        // center horizontally and vertically take all the available screen space:
                        <div className="flex-1 flex flex-col justify-center items-center bg-gray-50">
                            <div className="text-center max-w-md px-4">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <BiMessageAdd className="w-8 h-8 text-blue-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900 mb-2">Your messages</h2>
                                <p className="text-gray-600 mb-6">Send SMS and emails to your customers</p>
                                <button
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors duration-150 flex items-center mx-auto"
                                    onClick={() => this.openCustomerPicker()}
                                >
                                    <BiMessageAdd className="w-4 h-4 mr-2" />
                                    Send message
                                </button>
                                {isMobile && (
                                    <button
                                        onClick={this.toggleMobileSidebar}
                                        className="mt-4 text-blue-600 hover:text-blue-800 font-medium flex items-center mx-auto"
                                    >
                                        <Menu className="w-4 h-4 mr-2" />
                                        Show conversations
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {!!selectedConversation && (
                        <div className="flex flex-col h-full bg-white overflow-y-hidden">
                            {/* Chat header - only show on desktop */}
                            {!isMobile && (
                                <div className="bg-white border-b border-gray-200 p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <Avatar name={selectedConversation.customer?.fullName} round size={'40'} />
                                            <div>
                                                <h2 className="text-lg font-semibold text-gray-900">
                                                    {selectedConversation.customer?.fullName}
                                                </h2>
                                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                                    {selectedConversation.customer?.phoneNumbers?.map((number) => (
                                                        <span key={number.phoneNumber} className="flex items-center">
                                                            <Phone className="w-4 h-4 mr-1 text-gray-400" />
                                                            <a
                                                                href={`tel:${number.phoneNumber}`}
                                                                className="hover:text-blue-600 transition-colors duration-150"
                                                            >
                                                                {number.phoneNumber}
                                                            </a>
                                                        </span>
                                                    ))}
                                                    {!!selectedConversation.customer?.primaryEmail && (
                                                        <span className="flex items-center">
                                                            <Mail className="w-4 h-4 mr-1 text-gray-400" />
                                                            <a
                                                                href={`mailto:${selectedConversation.customer.primaryEmail}`}
                                                                className="text-blue-600 hover:text-blue-800 transition-colors duration-150"
                                                            >
                                                                {selectedConversation.customer.primaryEmail}
                                                            </a>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Active Orders Display */}
                                        <div className="flex items-center space-x-2">
                                            <div className="flex items-center space-x-1 text-gray-500">
                                                <span className="text-sm font-medium">Active Orders:</span>
                                            </div>
                                            <div className="flex space-x-1">
                                                {selectedConversation?.customer?.repairOrders?.slice(0, 4).map((RO) => (
                                                    <Link
                                                        to={`/repairorder/${RO.id}`}
                                                        key={RO.id}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <HoverTooltip
                                                            content={`#${RO.repairOrderNumber} - ${RO.title}`}
                                                        >
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors duration-150">
                                                                #{RO.repairOrderNumber}
                                                            </span>
                                                        </HoverTooltip>
                                                    </Link>
                                                )) || (
                                                    <span className="text-gray-400 text-sm">
                                                        No active repair orders
                                                    </span>
                                                )}
                                                {!selectedConversation?.customer?.repairOrders?.length && (
                                                    <span className="text-gray-400 text-sm">
                                                        No active repair orders
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Messages area */}
                            <div className="grow overflow-y-auto bg-gray-50 p-4 pb-2">
                                {!selectedConversation?.messages?.length && (
                                    <div className="flex flex-col justify-center items-center h-full">
                                        <div className="text-center">
                                            <Avatar name={selectedConversation.customer?.fullName} round size={'48'} />
                                            <h3 className="text-lg font-semibold text-gray-900 mb-1 mt-3">
                                                {selectedConversation.customer?.fullName}
                                            </h3>
                                            <p className="text-gray-500">Start the conversation</p>
                                        </div>
                                    </div>
                                )}
                                <div className="flex flex-col-reverse space-y-reverse space-y-4 overflow-y-auto h-full pr-4">
                                    {this.renderMessages()}
                                </div>
                            </div>

                            {/* Message input */}
                            <div className="bg-white border-t border-gray-200 p-4">
                                <div className="flex items-end space-x-3">
                                    <div className="flex-1">
                                        <div
                                            className="min-h-[44px] max-h-32 p-3 border border-gray-300 rounded-lg focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 bg-white overflow-y-auto"
                                            style={{ wordBreak: 'break-word' }}
                                        >
                                            <div
                                                contentEditable={true}
                                                id="text-message-content"
                                                className="outline-none text-gray-900 placeholder-gray-500"
                                                style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    minHeight: '20px',
                                                }}
                                                data-placeholder="Type your message..."
                                                onInput={() => {
                                                    const messageContent = document
                                                        .getElementById('text-message-content')
                                                        ?.innerText.trim();
                                                    this.setState({ isDisabled: !messageContent });
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => this.sendMessage()}
                                        disabled={this.state.isDisabled}
                                        className="p-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 text-white rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                                    >
                                        <IoMdSend className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default TextingPage;
